import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { Modal, Row, Col, Card, Form, Table } from "react-bootstrap";
import DashMedidasButton from "./DashMedidasButton/DashMedidasButton";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import { Line } from "react-chartjs-2";
import ChartDataLabels from 'chartjs-plugin-datalabels';
import Annotation from 'chartjs-plugin-annotation';
import dateFormat from "dateformat";
import PdfDownloader from './PdfDownloader';
import Financial from '../../../components/Financial/Financial';

// Registrar os plugins do Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartDataLabels,
  Annotation
);

const LimitsInputs = React.memo(({ tempLimits, handleLimitChange }) => (
  <>
    <hr style={{ color: "rgb(244, 244, 244)" }} />
    <h6 className="text-white mb-2">Limites de Umidade (%)</h6>
    <Row className="g-2 mb-2">
      <Col>
        <Form.Group>
          <Form.Label className="text-white small">Mínimo</Form.Label>
          <Form.Control
            type="number"
            step="0.1"
            value={tempLimits.min}
            onChange={(e) => handleLimitChange(e, 'min')}
            className="bg-dark text-white border-secondary"
            size="sm"
          />
        </Form.Group>
      </Col>
      <Col>
        <Form.Group>
          <Form.Label className="text-white small">Máximo</Form.Label>
          <Form.Control
            type="number"
            step="0.1"
            value={tempLimits.max}
            onChange={(e) => handleLimitChange(e, 'max')}
            className="bg-dark text-white border-secondary"
            size="sm"
          />
        </Form.Group>
      </Col>
    </Row>
  </>
));

const plugins = {
  afterDraw: function (chart) {
    if (chart.data.datasets.length > 0) {
      if (chart.data.datasets[0].data.length === 0) {
        var width = chart.width,
          height = chart.height,
          ctx = chart.ctx;
        ctx.restore();
        var fontSize = (height / 160).toFixed(2);
        ctx.font = fontSize + "em sans-serif";
        ctx.textBaseline = "top";
        var text = "Nenhuma Informação Disponível",
          textX = Math.round((width - ctx.measureText(text).width) / 2),
          textY = height / 2;
        ctx.fillText(text, textX, textY);
        ctx.save();
      }
    }
  },
}

const GraphCard = React.memo(({ type, data, options, color, limits, stats, mean }) => {
  const titles = {
    exact: `Medidas entre ${limits.min}% e ${limits.max}%`,
    below: `Medidas abaixo de ${limits.min}%`,
    above: `Medidas acima de ${limits.max}%`,
    high: `Rodizio`
  };

  const getStatsValue = (type) => {
    switch (type) {
      case 'exact':
        return {
          count: stats.exact,
          percentage: stats.getPercentage(stats.exact),
          mean: mean.exactMean,
          status: 'Dentro do Limite'
        };
      case 'below':
        return {
          count: stats.below,
          percentage: stats.getPercentage(stats.below),
          mean: mean.belowMean,
          status: 'Abaixo do Limite'
        };
      case 'above':
        return {
          count: stats.above,
          percentage: stats.getPercentage(stats.above),
          mean: mean.aboveMean,
          status: 'Acima do Limite'
        };
      case 'high':
        return {
          count: stats.high,
          percentage: stats.getPercentage(stats.high),
          mean: mean.highMean,
          status: 'Rodizio'
        };
      default:
        return { count: 0, percentage: 0, mean: 0, status: '' };
    }
  };

  const statsValue = getStatsValue(type);

  return (
    <Card className="shadow-sm border-secondary">
      <Card.Header className={`bg-${color} bg-opacity-75 py-1 text-center text-white`} >
        <h5 className="mb-0 fw-bold">{titles[type]}</h5>
      </Card.Header>
      <Card.Body>
        <Row>
          <Col xs={12} sm={12} md={4} lg={3} xl={2} xxl={2} className="border-end d-flex align-items-center">
            <div className="pe-3">
              <div className="mb-2">
                <h6 className="text-muted mb-1">Status</h6>
                <div className={`h4 text-${color} mb-0`}>{statsValue.status}</div>
              </div>
              <div className="mb-2">
                <h6 className="text-muted mb-1">Quantidade</h6>
                <div className={`h4 mb-0 text-${color}`}>{statsValue.count} Medidas</div>
                <small className={`text-dark`}>Percentual: <strong>({statsValue.percentage}%)</strong></small>
              </div>
              <div className="mb-2">
                <h6 className="text-muted mb-1">Média de Umidade</h6>
                <div className={`h4 mb-0 text-${color}`}>{statsValue.mean}%</div>
              </div>
            </div>
          </Col>

          <Col xs={12} sm={12} md={8} lg={9} xl={10} xxl={10}>
            <div style={{ height: 220 }}>
              <Line data={data} options={options} plugins={[plugins, ChartDataLabels]} />
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
});

const SimulationTable = React.memo(({ simulationData, currency = 'BRL' }) => {
  // Valores para os cálculos específicos
  const storageCapacity = parseFloat(simulationData.storageCapacity) || 0;

  // Calcular redução por umidade do grão
  const umidadeBreakReduction = simulationData.technicalBreakReduction || 0;
  const umidadeLossReduction = (umidadeBreakReduction / 100) * storageCapacity;
  const umidadeLossReductionBags = Math.round((umidadeLossReduction * 1000) / 60);

  // Calcular redução por respiração dos grãos
  // const respiracaoBreakReduction = simulationData.technicalBreakReduction - umidadeBreakReduction;
  // const respiracaoLossReduction = (respiracaoBreakReduction / 100) * storageCapacity;
  // const respiracaoLossReductionBags = Math.round((respiracaoLossReduction * 1000) / 60);

  // Valores totais
  const formattedFinancialReturn = simulationData.financialReturn.toLocaleString('pt-BR', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  const currencySymbol = currency === 'USD' ? 'US$' : 'R$';

  return (
    <div className="simulation-bootstrap">
      {/* DADOS DE ENTRADA */}
      <Card className="mb-2 bg-dark border-secondary">
        <Card.Header className="py-2 bg-success bg-opacity-25 text-center">
          <h6 className="mb-0 text-white fw-bold">DADOS DE ENTRADA</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Table style={{ fontSize: "0.8rem" }} className="table-dark" striped bordered hover responsive size='sm'>
            <tbody>
              <tr>
                <td className="ps-3 simulation-label">Capacidade total de armazenagem (toneladas)</td>
                <td className="pe-3 text-end fw-bold">{storageCapacity.toLocaleString('pt-BR')}</td>
              </tr>
              <tr>
                <td className="ps-3 simulation-label">Preço do grão ({currencySymbol}/saco)</td>
                <td className="pe-3 text-end fw-bold">{simulationData.sacaPriceValue.toLocaleString('pt-BR', { style: 'currency', currency, minimumFractionDigits: 2 })}</td>
              </tr>
              <tr>
                <td className="ps-3 simulation-label">Tempo de armazenamento (meses)</td>
                <td className="pe-3 text-end fw-bold">{simulationData.storageTimeValue}</td>
              </tr>
              <tr>
                <td className="ps-3 simulation-label">Umidade do grão padrão portaria comercialização (%)</td>
                <td className="pe-3 text-end fw-bold">{simulationData.standardHumidityValue}</td>
              </tr>
              <tr>
                <td className="ps-3 simulation-label">Umidade do grão após secagem - Média saída DRY (%)</td>
                <td className="pe-3 text-end fw-bold">{simulationData.means}</td>
              </tr>
              <tr>
                <td className="ps-3 simulation-label">Temperatura secagem - Média saída DRY (ºC)</td>
                <td className="pe-3 text-end fw-bold">{simulationData.dryingTemperatureValue}</td>
              </tr>
              <tr>
                <td className="ps-3 simulation-label">Temperatura de armazenamento silo (ºC)</td>
                <td className="pe-3 text-end fw-bold">{simulationData.storageTemperatureValue}</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* SIMULAÇÃO DA REDUÇÃO DE QUEBRA TÉCNICA */}
      <Card className="mb-2 bg-dark border-secondary">
        <Card.Header className="py-2 bg-success bg-opacity-25 text-center">
          <h6 className="mb-0 text-white fw-bold">SIMULAÇÃO DE QUEBRA TÉCNICA</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <Table style={{ fontSize: "0.8rem" }} className="table-dark" striped bordered hover responsive size='sm'>
            <tbody>
              <tr className="bg-success bg-opacity-10">
                <td className="ps-3 simulation-label">Redução de quebra técnica total (%)</td>
                <td className="pe-3 text-end fw-bold text-success">{simulationData.totalQuebra.toFixed(2)}</td>
              </tr>
              <tr className="bg-success bg-opacity-10">
                <td className="ps-3 simulation-label">Redução de perda (ton)</td>
                <td className="pe-3 text-end fw-bold text-success">{simulationData.totalPerda.toFixed(1)}</td>
              </tr>
              <tr className="bg-success bg-opacity-10">
                <td className="ps-3 simulation-label">Redução de perda (Sacos de 60 kg)</td>
                <td className="pe-3 text-end fw-bold text-success">{simulationData.perdaTotal.toFixed(0)}</td>
              </tr>
              <tr className="bg-success bg-opacity-10">
                <td className="ps-3 simulation-label">Em retorno financeiro total</td>
                <td className="pe-3 text-end fw-bold text-success">{formattedFinancialReturn}</td>
              </tr>
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {/* DETALHAMENTO DA QUEBRA TÉCNICA TOTAL */}
      <Card className="mb-2 bg-dark border-secondary">
        <Card.Header className="py-2 bg-success bg-opacity-25 text-center">
          <h6 className="mb-0 text-white fw-bold">DETALHAMENTO DA QUEBRA TÉCNICA TOTAL</h6>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="p-0">
            {/* Redução por umidade */}
            <div className="mb-1">
              <div className="p-2 bg-dark bg-opacity-75 border-bottom border-secondary">
                <h6 className="mb-0 text-white">Redução de quebra técnica - Umidade do grão na expedição</h6>
              </div>
              <Table style={{ fontSize: "0.8rem" }} className="table-dark" striped bordered hover responsive size='sm'>
                <tbody>
                  <tr>
                    <td className="ps-3 simulation-label">Redução de quebra técnica (%)</td>
                    <td className="pe-3 text-end fw-bold">{umidadeBreakReduction.toFixed(4)}</td>
                  </tr>
                  <tr>
                    <td className="ps-3 simulation-label">Redução de perda (ton)</td>
                    <td className="pe-3 text-end fw-bold">{umidadeLossReduction.toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td className="ps-3 simulation-label">Redução de perda (Sacos de 60 kg)</td>
                    <td className="pe-3 text-end fw-bold">{umidadeLossReductionBags.toLocaleString('pt-BR')}</td>
                  </tr>
                </tbody>
              </Table>
            </div>

            {/* Redução por respiração */}
            <div>
              <div className="p-2 bg-dark bg-opacity-75 border-bottom border-secondary">
                <h6 className="mb-0 text-white">Redução de quebra técnica - Respiração dos grãos</h6>
              </div>
              <Table style={{ fontSize: "0.8rem" }} className="table-dark" striped bordered hover responsive size='sm'>
                <tbody>
                  <tr>
                    <td className="ps-3 simulation-label">Redução de quebra técnica (%)</td>
                    <td className="pe-3 text-end fw-bold">{simulationData.lossReductionResp2.toFixed(4)}</td>
                  </tr>
                  <tr>
                    <td className="ps-3 simulation-label">Redução de perda (ton)</td>
                    <td className="pe-3 text-end fw-bold">{simulationData.lossReductionResp.toFixed(1)}</td>
                  </tr>
                  <tr>
                    <td className="ps-3 simulation-label">Redução de perda (Sacos de 60 kg)</td>
                    <td className="pe-3 text-end fw-bold">{simulationData.reduceBags.toFixed(0)}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </div>
        </Card.Body>
      </Card>

    </div>
  );
});

// Componente WeightResults que estava faltando
const WeightResults = React.memo(({ weightStats, currency = 'BRL' }) => (
  <Card className="shadow-sm border-secondary mb-3">
    <Card.Header className="py-2 border-secondary bg-dark">
      <h6 className="mb-1 mt-1 text-white fw-bold text-center">SIMULAÇÃO DE QUEBRA TÉCNICA</h6>
    </Card.Header>
    <Card.Body className="bg-dark p-1">
      <SimulationTable simulationData={weightStats} currency={currency} />
    </Card.Body>
  </Card>
));

const GrainPricesModal = React.memo(({ show, onHide }) => {
  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static" className="dash-medidas-modal">
      <Modal.Header closeButton className="bg-dark border-secondary py-2">
        <Modal.Title className="text-white" style={{ fontSize: 16 }}>Preços de Grãos</Modal.Title>
      </Modal.Header>
      <Modal.Body className="p-0 bg-dark">
        <div className="p-2">
          <Financial setLoading={() => { }} />
        </div>
      </Modal.Body>
      <Modal.Footer className="bg-dark border-secondary py-2">
        <DashMedidasButton variant="outline-light" onClick={onHide}>
          Fechar
        </DashMedidasButton>
      </Modal.Footer>
    </Modal>
  );
});

const FullScreenModal = ({
  user,
  getMaxDate,
  getMinDate,
  show,
  handleClose,
  data,
  permissionSGU,
  startDate,
  endDate,
  setStartDate,
  setEndDate,
  curvaSelecionada,
  handleChange,
  curvas,
  empresa,
  serialnumber,
}) => {
  // Adicionar estados para max e min
  const [maxValue, setMaxValue] = useState(35);
  const [minValue, setMinValue] = useState(5);

  // Adicionar contentRef logo após os estados existentes
  const contentRef = useRef(null);

  // Função para calcular max e min foi movida para useEffect

  const handleChangeStartDate = useCallback((event) => {
    const rawDate = new Date(event.target.value);
    rawDate.setHours(0, 0, 0, 0); // Define para 00:00:00
    setStartDate(event.target.value); // Converte para ISO para padronizar
  }, [setStartDate]);

  const handleChangeEndDate = useCallback((event) => {
    const rawDate = new Date(event.target.value);
    rawDate.setHours(23, 59, 59, 999); // Define para 23:59:59.999
    setEndDate(event.target.value); // Converte para ISO para padronizar
  }, [setEndDate]);

  const [limits, setLimits] = useState({
    min: 13.5,
    max: 14.5
  });

  const [tempLimits, setTempLimits] = useState({
    min: 13.5,
    max: 14.5
  });

  const [mean, setMean] = useState({
    exactMean: 0,
    belowMean: 0,
    aboveMean: 0,
    highMean: 0
  });

  // Referência para armazenar o timeout
  const debounceTimerRef = useRef(null);

  // Função debounce mais robusta
  const debouncedUpdateLimits = useCallback((newLimits) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      setLimits(newLimits);
      debounceTimerRef.current = null;
    }, 800);
  }, []);

  const handleLimitChange = useCallback((e, type) => {
    const newValue = parseFloat(e.target.value);
    setTempLimits((prevLimits) => {
      let updatedLimits = { ...prevLimits, [type]: newValue };

      if (updatedLimits.min > updatedLimits.max) {
        if (type === "min") {
          updatedLimits.min = updatedLimits.max;
        } else {
          updatedLimits.max = updatedLimits.min;
        }
      }

      debouncedUpdateLimits(updatedLimits);
      return updatedLimits;
    });
  }, [debouncedUpdateLimits]);

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Atualizar commonOptions para usar os novos valores
  const commonOptions = useMemo(() => ({
    responsive: true,
    maintainAspectRatio: false,
    animation: {
      duration: 800,
    },
    elements: {
      point: {
        radius: 4,
        hitRadius: 10,
        hoverRadius: 6,
      },
      line: {
        tension: 0.3,
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#000',
          maxTicksLimit: 20,
          font: {
            size: 12,
          }
        }
      },
      y: {
        max: maxValue + 3,
        min: minValue - 3,
        stepSize: 5,
        grid: {
          color: 'rgba(0, 0, 0, 0.1)',
        },
        ticks: {
          color: '#000',
          font: {
            size: 12,
          }
        },
        beginAtZero: true
      }
    },
    plugins: {
      legend: {
        position: 'top',
        labels: {
          color: '#1a1a1a',
          padding: 20,
          font: {
            size: 13,
            weight: 'bold'
          }
        }
      },
      datalabels: {
        display: "auto",
        color: '#000',
        align: 'top',
        padding: 4,
        formatter: (value) => value.toFixed(1) + '%',
        font: {
          weight: 'bold',
          size: 11
        },
        backgroundColor: 'rgba(255, 255, 255, 0.7)',
        borderRadius: 4
      },
      tooltip: {
        backgroundColor: '#212529',
        titleFont: {
          size: 14,
        },
        bodyFont: {
          size: 13,
        },
        borderColor: '#ccc',
        borderWidth: 1,
        padding: 12,
        cornerRadius: 6,
        displayColors: true
      }
    }
  }), [maxValue, minValue]); // Adicionar dependências

  // Modificar processData para atualizar min/max
  const processData = useCallback(() => {
    // Nova categoria para Rodizio (baseada na flag rotation)
    const highRange = data.filter(item => item.rotation === true || item.rotation === "true");

    const exactRange = data.filter(item => {
      if (item.rotation === true || item.rotation === "true") return false;
      const humidity = parseFloat(item.humidity);
      return humidity >= limits.min && humidity <= limits.max;
    });

    const belowRange = data.filter(item => {
      if (item.rotation === true || item.rotation === "true") return false;
      return parseFloat(item.humidity) < limits.min;
    });

    // Modificar a categoria "above" para mostrar valores acima do limite máximo (excluindo Rodizio)
    const aboveRange = data.filter(item => {
      if (item.rotation === true || item.rotation === "true") return false;
      const humidity = parseFloat(item.humidity);
      return humidity > limits.max;
    });

    const calculateMeanForRange = (range) => {
      if (range.length === 0) return 0;
      const sum = range.reduce((acc, item) => acc + parseFloat(item.humidity), 0);
      return (sum / range.length).toFixed(1);
    };

    const exactMean = calculateMeanForRange(exactRange);
    const belowMean = calculateMeanForRange(belowRange);
    const aboveMean = calculateMeanForRange(aboveRange);
    const highMean = calculateMeanForRange(highRange);

    setMean({
      exactMean,
      belowMean,
      aboveMean,
      highMean
    });

    const result = {
      exact: {
        labels: exactRange.map(item => dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM")),
        datasets: [{
          label: `Umidade entre ${limits.min}% e ${limits.max}% (Média: ${exactMean}%)`,
          data: exactRange.map(item => parseFloat(item.humidity)),
          borderColor: '#198754',
          backgroundColor: '#19875430',
          fill: true,
          datalabels: {
            backgroundColor: 'rgba(25, 135, 84, 0.7)',
            color: '#1A1A1A'
          }
        }]
      },
      below: {
        labels: belowRange.map(item => dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM")),
        datasets: [{
          label: `Umidade abaixo de ${limits.min}% (Média: ${belowMean}%)`,
          data: belowRange.map(item => parseFloat(item.humidity)),
          borderColor: '#dc3545',
          backgroundColor: '#dc354530',
          fill: true,
          datalabels: {
            backgroundColor: 'rgba(220, 53, 69, 0.7)',
            color: '#1A1A1A'
          }
        }]
      },
      above: {
        labels: aboveRange.map(item => dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM")),
        datasets: [{
          label: `Umidade acima de ${limits.max}% (Média: ${aboveMean}%)`,
          data: aboveRange.map(item => parseFloat(item.humidity)),
          borderColor: '#ffc107',
          backgroundColor: '#ffc10730',
          fill: true,
          datalabels: {
            backgroundColor: 'rgba(255, 193, 7, 0.7)',
            color: '#1A1A1A'
          }
        }]
      },
      high: {
        labels: highRange.map(item => dateFormat(item.date, "UTC:dd/mm/yy' 'HH:MM")),
        datasets: [{
          label: `Rodizio (Média: ${highMean}%)`,
          data: highRange.map(item => parseFloat(item.humidity)),
          borderColor: '#0d6efd',
          backgroundColor: '#0d6efd30',
          fill: true,
          datalabels: {
            backgroundColor: 'rgba(13, 110, 253, 0.7)',
            color: '#1A1A1A'
          }
        }]
      }
    };

    return result;
  }, [data, limits]);

  const chartData = useMemo(() => processData(), [processData]);

  // UseEffect para atualizar min/max quando os dados originais mudam
  useEffect(() => {
    if (!data || data.length === 0) return;

    // Calcular min/max sem usar spread operator (evita stack overflow em arrays grandes)
    let max = -Infinity;
    let min = Infinity;
    
    for (const item of data) {
      const humidity = parseFloat(item.humidity);
      if (!isNaN(humidity)) {
        if (humidity > max) max = humidity;
        if (humidity < min) min = humidity;
      }
    }

    if (max !== -Infinity && min !== Infinity) {
      setMaxValue(max);
      setMinValue(min);
    }
  }, [data]); // Depende apenas de 'data', não de chartData

  const stats = useMemo(() => ({
    exact: chartData.exact.datasets[0].data.length,
    below: chartData.below.datasets[0].data.length,
    above: chartData.above.datasets[0].data.length,
    high: chartData.high.datasets[0].data.length,
    total: data.length,
    getPercentage: (value) => ((value / data.length) * 100).toFixed(1)
  }), [chartData, data.length]);

  // Remover do dataset bruto os registros que se enquadram em "Rodizio" (flag rotation) para o PDF
  const filteredDataNoRodizio = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.filter(item => !(item.rotation === true || item.rotation === "true"));
  }, [data]);

  // Funções utilitárias adicionais removidas por não uso

  const calculateMean = useCallback((data) => {
    if (!data || data.length === 0) return 0;
    const sum = data.reduce((acc, val) => acc + val, 0);
    return (sum / data.length).toFixed(1);
  }, []);

  const getGraphOptions = useCallback((type, data) => {
    const baseOptions = { ...commonOptions };
    const meanValue = calculateMean(data.datasets[0].data);

    baseOptions.plugins = {
      ...baseOptions.plugins,
      annotation: {
        annotations: {
          minLine: {
            type: 'line',
            yMin: limits.min,
            yMax: limits.min,
            borderColor: 'red',
            borderWidth: 1,
            borderDash: [5, 5],
            label: {
              content: `Min: ${limits.min}%`,
              enabled: true,
              position: 'start',
              backgroundColor: 'red',
              color: 'white',
              padding: 4,
              font: {
                weight: 'bold'
              }
            }
          },
          maxLine: {
            type: 'line',
            yMin: limits.max,
            yMax: limits.max,
            borderColor: 'yellow',
            borderWidth: 1,
            borderDash: [5, 5],
            label: {
              content: `Max: ${limits.max}%`,
              enabled: true,
              position: 'end',
              backgroundColor: 'yellow',
              color: 'white',
              padding: 4,
              font: {
                weight: 'bold'
              }
            }
          },
          meanLine: {
            type: 'line',
            yMin: meanValue,
            yMax: meanValue,
            borderColor: '#1c4220',
            borderWidth: 2,
            borderDash: [3, 3],
            label: {
              content: `Média: ${meanValue}%`,
              enabled: true,
              position: 'center',
              backgroundColor: 'rgba(75, 192, 192, 0.8)',
              color: '#1c4220',
              padding: 4,
              font: {
                weight: 'bold'
              }
            }
          }
        }
      }
    };

    return baseOptions;
  }, [commonOptions, limits, calculateMean]);

  // Adicionar estado para peso
  const [weight] = useState("");

  // Adicionar estados para os novos campos
  const [sacaPrice, setSacaPrice] = useState("");
  const [storageCapacity, setStorageCapacity] = useState("");
  const [storageTime, setStorageTime] = useState("");
  const [standardHumidity, setStandardHumidity] = useState("14");
  const [postDryingHumidity, setPostDryingHumidity] = useState("");
  const [dryingTemperature, setDryingTemperature] = useState("20");
  const [storageTemperature, setStorageTemperature] = useState("20");
  const [currency, setCurrency] = useState('BRL');
  const [showPricesModal, setShowPricesModal] = useState(false);

  // Input de peso está desabilitado na UI; manteremos somente o estado

  // Funções para lidar com mudanças nos novos campos
  const handleSacaPriceChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setSacaPrice(value);
  }, []);

  const handleStorageCapacityChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setStorageCapacity(value);
  }, []);

  const handleStorageTimeChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setStorageTime(value);
  }, []);

  const handleStandardHumidityChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setStandardHumidity(value);
  }, []);

  const handlePostDryingHumidityChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setPostDryingHumidity(value);
  }, []);

  const handleDryingTemperatureChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setDryingTemperature(value);
  }, []);

  const handleStorageTemperatureChange = useCallback((e) => {
    const value = e.target.value.replace(/[^0-9.]/g, '');
    setStorageTemperature(value);
  }, []);

  const handleCurrencyChange = useCallback((e) => {
    setCurrency(e.target.value);
  }, []);

  // Calcular estatísticas de peso e temperaturas de aeração
  const weightStats = useMemo(() => {
    const totalWeight = parseFloat(weight) || 0;
    const exactPercent = stats.getPercentage(stats.exact);
    const outOfRangePercent = (100 - parseFloat(exactPercent));
    const sacaPriceValue = parseFloat(sacaPrice) || 0;
    const standardHumidityValue = parseFloat(standardHumidity) || 14;
    const postDryingHumidityValue = parseFloat(postDryingHumidity) || 0;
    const storageTimeValue = parseFloat(storageTime) || 0;
    const dryingTemperatureValue = parseFloat(dryingTemperature) || 0;
    const storageTemperatureValue = parseFloat(storageTemperature) || 0;
    const storageCapacityValue = parseFloat(storageCapacity) || 0;

    const means = postDryingHumidityValue || parseFloat(mean.belowMean) || parseFloat(mean.exactMean) || parseFloat(mean.aboveMean)

    // Média da umidade de saída
    const averageExitHumidity = parseFloat(mean.belowMean) || parseFloat(mean.exactMean) || 0;

    // Perda total de massa de grão
    const totalMassLoss = (100 * (standardHumidityValue - 14)) / (100 - 14);

    // Perda automática de massa
    const automaticMassLoss = (100 * (14 - averageExitHumidity)) / (100 - averageExitHumidity);

    // Redução da quebra técnica
    const technicalBreakReduction = totalMassLoss - automaticMassLoss;

    // Temperatura de aeração manual
    const manualAerationTemp = 168.72 - (0.184 * storageTimeValue) - (0.131 * dryingTemperatureValue);

    // Temperatura de aeração automática
    const autoAerationTemp = 168.72 - (0.184 * storageTimeValue) - (0.131 * storageTemperatureValue);

    // Cálculo da redução da perda em toneladas usando a capacidade total de armazenagem
    const lossReduction = (technicalBreakReduction / 100) * storageCapacityValue;

    // Redução de perda (Sacos de 60 kg): (Redução de perda (ton)*1000)/60
    const lossReductionBags = (lossReduction * 1000) / 60;

    //Redução de quebra técnica (%)
    const lossReductionResp2 = ((autoAerationTemp - manualAerationTemp) / manualAerationTemp) * 100;

    //Redução de perda (ton)
    const lossReductionResp = (lossReductionResp2 / 100) * storageCapacity;

    //Redução de perda (Sacos de 60 kg)
    const reduceBags = (lossReductionResp * 1000) / 60;

    // Perda total de massa de grão
    const perdaTotal = lossReductionBags + reduceBags;

    const totalPerda = lossReduction + lossReductionResp;

    const totalQuebra = technicalBreakReduction + lossReductionResp2;

    // Cálculo do retorno financeiro baseado nos sacos economizados
    const financialReturn = perdaTotal * sacaPriceValue;

    return {
      total: totalWeight,
      inRange: (totalWeight * parseFloat(exactPercent)) / 100,
      outRange: (totalWeight * outOfRangePercent) / 100,
      inRangePercent: exactPercent,
      outRangePercent: outOfRangePercent.toFixed(1),
      technicalBreakReduction,
      automaticMassLoss,
      lossReduction,
      lossReductionBags,
      perdaTotal,
      financialReturn,
      totalMassLoss,
      manualAerationTemp,
      autoAerationTemp,
      storageCapacity: storageCapacityValue,
      storageTimeValue,
      standardHumidityValue,
      means,
      dryingTemperatureValue,
      storageTemperatureValue,
      sacaPriceValue,
      lossReductionResp2,
      lossReductionResp,
      reduceBags,
      totalPerda,
      totalQuebra
    };
  }, [
    weight,
    stats,
    sacaPrice,
    standardHumidity,
    postDryingHumidity,
    storageTime,
    dryingTemperature,
    storageTemperature,
    storageCapacity,
    mean,
  ]);


  return (
    <Modal show={show} onHide={handleClose} fullscreen className="bg-dark dash-medidas-modal">
      <Modal.Header className="border-bottom border-secondary" style={{ background: '#1a1a1a', padding: 12 }}>
        <Modal.Title className="text-white">Análise de Umidade por Faixas</Modal.Title>
        <div className="d-flex gap-2"> {/* Adicionado d-flex e gap-2 para espaçamento entre botões */}
          <PdfDownloader
            user={user}
            empresa={empresa}
            serialnumber={serialnumber}
            // Enviar dados sem a categoria "Rodizio" (flag rotation)
            data={filteredDataNoRodizio}
            startDate={startDate}
            endDate={endDate}
            limits={limits}
            stats={stats}
            mean={mean}
            weight={weight}
            weightStats={weightStats}
            curvaSelecionada={curvaSelecionada}
            contentRef={contentRef}
            sacaPrice={sacaPrice}
            storageCapacity={storageCapacity}
            storageTime={storageTime}
            standardHumidity={standardHumidity}
            postDryingHumidity={postDryingHumidity}
            dryingTemperature={dryingTemperature}
            storageTemperature={storageTemperature}
            currency={currency}
            permissionSGU={permissionSGU}
          />
          <DashMedidasButton variant="outline-warning" onClick={handleClose} className="px-2">
            X
          </DashMedidasButton>
        </div>
      </Modal.Header>
      <Modal.Body className="p-0" style={{ background: '#212121' }}>
        <div ref={contentRef}>
          <Row className="g-0 h-100">

            <Col
              xs={12}
              md={4}
              lg={3}
              xl={3}
              xxl={3}
              className="border-end border-secondary dash-modal-sidebar"
              style={{
                height: 'calc(100vh - 60px)',
                background: '#0C392E'
              }}
            >

              <div className="p-3 h-100 d-flex flex-column" style={{ overflowY: 'auto' }}>
                <h5 className="mb-2 text-white text-center">{user?.hideSensitiveInfo ? '***' : empresa}</h5>
                <h6 className="mb-0 text-white text-center">{data?.length && data[data.length - 1]?.nameEquipament ? data[data.length - 1]?.nameEquipament : serialnumber}</h6>

                <hr style={{ color: "rgb(244, 244, 244)", marginBottom: 5, marginTop: 5 }} />
                <h6 className="m-0 p-0 text-center" style={{ color: "#8fad15" }}>Total de Medidas: {data?.length}</h6>
                <hr style={{ color: "rgb(244, 244, 244)", marginBottom: 5, marginTop: 8 }} />

                <h5 className="mb-2 text-white">Filtros</h5>
                <Form>
                  <div className="sticky-top">

                    <Form.Group className="mb-3">
                      <Form.Label className="text-white small">Data Inicial</Form.Label>
                      <Form.Control
                        className="bg-dark text-white border-secondary"
                        type="date"
                        value={startDate ? startDate.slice(0, 10) : ''}
                        onChange={handleChangeStartDate}
                        disabled={!permissionSGU?.home?.write}
                        min={getMinDate()}
                        max={getMaxDate()}
                        size="sm"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label className="text-white small">Data Final</Form.Label>
                      <Form.Control
                        className="bg-dark text-white border-secondary"
                        type="date"
                        value={endDate ? endDate.slice(0, 10) : ''}
                        onChange={handleChangeEndDate}
                        disabled={!permissionSGU?.home?.write}
                        min={getMinDate()}
                        max={getMaxDate()}
                        size="sm"
                      />
                    </Form.Group>

                    <Form.Group className="mb-2">
                      <Form.Label className="text-white small">Curva</Form.Label>
                      <Form.Select
                        aria-label="Default select example"
                        className="bg-dark text-white border-secondary"
                        id="demo-simple-select"
                        value={curvaSelecionada}
                        onChange={handleChange}
                        disabled={!permissionSGU?.home?.write}
                        size="sm"
                      >
                        {curvas ? curvas.map((item) => (
                          <option key={item} value={item}>{item}</option>
                        )) : null}
                      </Form.Select>
                    </Form.Group>

                    <LimitsInputs tempLimits={tempLimits} handleLimitChange={handleLimitChange} />

                    <hr style={{ color: "rgb(244, 244, 244)" }} />
                    <h6 className="text-white mb-2">Dados de Produção</h6>
                    <Row className="g-2">
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small">Moeda</Form.Label>
                          <Form.Select
                            className="bg-dark text-white border-secondary"
                            value={currency}
                            onChange={handleCurrencyChange}
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          >
                            <option value="BRL">Real (BRL)</option>
                            <option value="USD">Dólar (USD)</option>
                          </Form.Select>
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      {/* <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small">Peso (ton)</Form.Label>
                          <Form.Control
                            type="text"
                            className="bg-dark text-white border-secondary"
                            value={weight}
                            onChange={handleWeightChange}
                            placeholder="Peso total"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col> */}
                      <Col md={12}>
                        <Form.Group className="mb-3">
                          <div className="d-flex justify-content-between align-items-center">
                            <Form.Label className="text-white small m-0">Preço ({currency === 'USD' ? 'US$' : 'R$'}/saca)</Form.Label>
                            <DashMedidasButton
                              variant="outline-info"
                              onClick={() => setShowPricesModal(true)}
                            >
                              Ver preços
                            </DashMedidasButton>
                          </div>
                          <Form.Control
                            type="number"
                            className="bg-dark text-white border-secondary"
                            value={sacaPrice}
                            onChange={handleSacaPriceChange}
                            placeholder="Preço da saca"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small">Capacidade (ton)</Form.Label>
                          <Form.Control
                            type="number"
                            className="bg-dark text-white border-secondary"
                            value={storageCapacity}
                            onChange={handleStorageCapacityChange}
                            placeholder="Capacidade total"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small">Tempo (meses)</Form.Label>
                          <Form.Control
                            type="number"
                            className="bg-dark text-white border-secondary"
                            value={storageTime}
                            onChange={handleStorageTimeChange}
                            placeholder="Tempo de armazenamento"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <hr style={{ color: "rgb(244, 244, 244)" }} />
                    <h6 className="text-white mb-2">Parâmetros de Secagem e Armazenamento</h6>

                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small">Umidade Padrão (%)</Form.Label>
                          <Form.Control
                            type="number"
                            className="bg-dark text-white border-secondary"
                            value={standardHumidity}
                            onChange={handleStandardHumidityChange}
                            placeholder="14%"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small">Pós Secagem (%)</Form.Label>
                          <Form.Control
                            type="number"
                            className="bg-dark text-white border-secondary"
                            value={postDryingHumidity || parseFloat(mean.belowMean) || parseFloat(mean.exactMean) || parseFloat(mean.aboveMean)}
                            onChange={handlePostDryingHumidityChange}
                            placeholder="Umidade após secagem"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <Row className="g-2">
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small" title="Temperatura de Secagem °C">Temp. Secagem (°C)</Form.Label>
                          <Form.Control
                            type="number"
                            className="bg-dark text-white border-secondary"
                            value={dryingTemperature}
                            onChange={handleDryingTemperatureChange}
                            placeholder="Temperatura"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Label className="text-white small" title="Temperatura de Armazenamento °C">Temp. Armaz. (°C)</Form.Label>
                          <Form.Control
                            type="number"
                            className="bg-dark text-white border-secondary"
                            value={storageTemperature}
                            onChange={handleStorageTemperatureChange}
                            placeholder="Temperatura"
                            disabled={!permissionSGU?.home?.write}
                            size="sm"
                          />
                        </Form.Group>
                      </Col>
                    </Row>

                    <WeightResults weightStats={weightStats} currency={currency} />

                  </div>
                </Form>
              </div>
            </Col>

            <Col
              xs={12}
              md={8}
              lg={9}
              xl={9}
              xxl={9}
              className="dash-modal-content"
              style={{
                height: 'calc(100vh - 60px)',
                overflowY: 'auto',
                overflowX: 'hidden',
                background: '#0C392E'
              }}
            >
              <div className="p-3">
                <div className="d-flex flex-column gap-2">

                  <GraphCard
                    type="above"
                    data={chartData.above}
                    options={getGraphOptions('above', chartData.above)}
                    color="warning"
                    limits={limits}
                    stats={stats}
                    mean={mean}
                  />

                  <GraphCard
                    type="exact"
                    data={chartData.exact}
                    options={getGraphOptions('exact', chartData.exact)}
                    color="success"
                    limits={limits}
                    stats={stats}
                    mean={mean}
                  />

                  <GraphCard
                    type="below"
                    data={chartData.below}
                    options={getGraphOptions('below', chartData.below)}
                    color="danger"
                    limits={limits}
                    stats={stats}
                    mean={mean}
                  />

                  <GraphCard
                    type="high"
                    data={chartData.high}
                    options={getGraphOptions('high', chartData.high)}
                    color="primary"
                    limits={limits}
                    stats={stats}
                    mean={mean}
                  />

                </div>
              </div>
            </Col>
          </Row>
          <GrainPricesModal
            show={showPricesModal}
            onHide={() => setShowPricesModal(false)}
            currency={currency}
            sacaPrice={sacaPrice}
            curvaSelecionada={curvaSelecionada}
          />
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default React.memo(FullScreenModal);
